import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import {
    Avatar,
    Badge,
    Box,
    Checkbox,
    Group,
    Loader,
    Pagination,
    Table,
    Text,
    UnstyledButton,
} from "@mantine/core";
import React, { useMemo, useState } from "react";

export interface TechnologyReportTableRow {
    personId: number;
    firstName: string;
    lastName: string;
    email: string;
    thumbnailBase64?: string | null;
    matchedTechnologyNames: string[];
}

export interface TechnologyReportResultsTableProps {
    rows: TechnologyReportTableRow[];
    loading?: boolean;
    selectedIds: Set<number>;
    onToggleSelect: (personId: number) => void;
    onSelectAllOnPage: () => void;
    onDeselectAllOnPage: () => void;
    allSelectedOnPage: boolean;
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

type SortDirection = "asc" | "desc";

export const TechnologyReportResultsTable: React.FC<TechnologyReportResultsTableProps> = ({
    rows,
    loading = false,
    selectedIds,
    onToggleSelect,
    onSelectAllOnPage,
    onDeselectAllOnPage,
    allSelectedOnPage,
    page,
    totalPages,
    onPageChange,
}) => {
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

    const toggleSort = () => setSortDirection((d) => (d === "asc" ? "desc" : "asc"));

    const sortedRows = useMemo(() => {
        return [...rows].sort((a, b) => {
            const cmp = `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
            return sortDirection === "asc" ? cmp : -cmp;
        });
    }, [rows, sortDirection]);

    if (loading) {
        return (
            <Box p="xl" style={{ textAlign: "center" }}>
                <Loader size="md" color="brand" />
            </Box>
        );
    }

    if (rows.length === 0) {
        return (
            <Box p="xl" style={{ textAlign: "center" }}>
                <Text size="lg" fw={500} c="dimmed">
                    No persons match this filter.
                </Text>
            </Box>
        );
    }

    return (
        <Box>
            <Table.ScrollContainer minWidth={520}>
                <Table verticalSpacing="sm" striped highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th w={44}>
                                <Checkbox
                                    checked={allSelectedOnPage}
                                    onChange={() => (allSelectedOnPage ? onDeselectAllOnPage() : onSelectAllOnPage())}
                                    aria-label="Select all on this page"
                                />
                            </Table.Th>
                            <Table.Th w={44} />
                            <Table.Th>
                                <UnstyledButton onClick={toggleSort} aria-label="Sort by name">
                                    <Group gap={4} wrap="nowrap">
                                        <Text fw={600} size="sm">Name</Text>
                                        {sortDirection === "asc"
                                            ? <ChevronUpIcon style={{ width: 12, height: 12 }} />
                                            : <ChevronDownIcon style={{ width: 12, height: 12 }} />}
                                    </Group>
                                </UnstyledButton>
                            </Table.Th>
                            <Table.Th>Email</Table.Th>
                            <Table.Th>Technologies</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {sortedRows.map((row) => {
                            const isSelected = selectedIds.has(row.personId);
                            const fullName = `${row.firstName} ${row.lastName}`;
                            const initials = `${row.firstName.charAt(0)}${row.lastName.charAt(0)}`;
                            return (
                                <Table.Tr
                                    key={row.personId}
                                    onClick={() => onToggleSelect(row.personId)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <Table.Td onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={() => onToggleSelect(row.personId)}
                                            aria-label={`Select ${fullName}`}
                                        />
                                    </Table.Td>
                                    <Table.Td>
                                        <Avatar
                                            size={32}
                                            radius="xl"
                                            color="brand"
                                            src={row.thumbnailBase64 ? `data:image/jpeg;base64,${row.thumbnailBase64}` : undefined}
                                        >
                                            {!row.thumbnailBase64 && initials}
                                        </Avatar>
                                    </Table.Td>
                                    <Table.Td>
                                        <Text fw={500} style={{ whiteSpace: "nowrap" }}>
                                            {fullName}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td>{row.email}</Table.Td>
                                    <Table.Td>
                                        <Group gap={4} wrap="wrap">
                                            {row.matchedTechnologyNames.map((name) => (
                                                <Badge key={name} size="sm" variant="outline" color="sopraPurple">
                                                    {name}
                                                </Badge>
                                            ))}
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            );
                        })}
                    </Table.Tbody>
                </Table>
            </Table.ScrollContainer>

            {totalPages > 1 && (
                <Group justify="center" mt="md">
                    <Pagination value={page} onChange={onPageChange} total={totalPages} />
                </Group>
            )}
        </Box>
    );
};
